import { UserProfile, CourtSurface } from './types';
export type { UserProfile };

const STORAGE_KEY = 'joy_tennis_user_profile_v1';

export interface NTRPQuestion {
  id: number;
  question: string;
  questionEn: string;
  options: Array<{
    text: string;
    textEn: string;
    points: number; // Contribution to NTRP score
  }>;
}

export const NTRP_QUIZ: NTRPQuestion[] = [
  {
    id: 1,
    question: '在底线正手对拉相持中，你的表现最符合以下哪项？',
    questionEn: 'In baseline forehand rallies, which best describes you?',
    options: [
      { text: '经常漏球或打在拍框上，需要对方喂到手边才能击打 (NTRP 1.5 - 2.0)', textEn: 'Frequently miss or frame the ball, need balls fed to hand', points: 1.8 },
      { text: '能进行中等速度的正手相持，但难以控制深度和方向 (NTRP 2.5 - 3.0)', textEn: 'Can sustain medium pace rally but lack control of depth and direction', points: 2.8 },
      { text: '正手有稳定上旋和良好深度，能在被动中自救，偶尔打出进攻制胜分 (NTRP 3.5 - 4.0)', textEn: 'Consistent topspin & depth, good defensive recovery, occasional winners', points: 3.8 },
      { text: '正手是极具统治力的重炮武器，能随时变线，控制旋转与落点 (NTRP 4.5+)', textEn: 'Dominant weapon with pace, spin, and down-the-line accuracy', points: 4.8 }
    ]
  },
  {
    id: 2,
    question: '关于发球与双误，你通常的比赛表现如何？',
    questionEn: 'Regarding your serve and double faults in matches?',
    options: [
      { text: '发球多为推球或碰球入界，经常出现连续双误 (NTRP 2.0)', textEn: 'Pushing ball into court, frequent double faults', points: 2.0 },
      { text: '一发有一定力量但进球率不稳定，二发多为减速软推 (NTRP 3.0)', textEn: 'Moderate first serve pace, but second serve is a weak push', points: 3.0 },
      { text: '一发二发动作协调，二发有稳定上旋/侧旋，极少送出双误 (NTRP 3.5 - 4.0)', textEn: 'Reliable kick/slice second serve with rarely any double faults', points: 3.8 },
      { text: '发球可以精确打击内外角 T 点，一发时速高且具备战术 Serve+1 抢攻能力 (NTRP 4.5+)', textEn: 'Pinpoint placement on T and wide, weapons on both serves', points: 4.7 }
    ]
  },
  {
    id: 3,
    question: '关于网前截击与高压球（Volley & Overhead）？',
    questionEn: 'Regarding your volleys and overheads at the net?',
    options: [
      { text: '害怕上网，网前容易被球打到或漏球 (NTRP 2.0)', textEn: 'Reluctant to go to net, lack footwork or miss volleys', points: 2.0 },
      { text: '能截击较舒适的中高球，但在处理低球或快速穿越球时失误偏多 (NTRP 3.0)', textEn: 'Can put away easy floaters, but struggle with low dips or passing shots', points: 3.0 },
      { text: '网前截击干净果断，掌握脚下切削截击与高压扣杀终结能力 (NTRP 3.5 - 4.0)', textEn: 'Crisp volleys with underspin, put-away overheads on lobs', points: 3.9 },
      { text: '具备职业/高阶双打网前统治力，抢网（Poach）意识敏锐，封堵范围极大 (NTRP 4.5+)', textEn: 'Elite poaching, reflex volleys, and touch drop volleys under pressure', points: 4.8 }
    ]
  },
  {
    id: 4,
    question: '在战术意识与实战抗压能力方面？',
    questionEn: 'Regarding your tactical awareness and clutch mental play?',
    options: [
      { text: '主要依靠本能接球，不清楚沃德洛法则或双打站位 (NTRP 2.0)', textEn: 'Play by instinct, unfamiliar with tactical directionals or positioning', points: 2.0 },
      { text: '开始尝试调动对手，但面对关键分容易手紧或心态波动 (NTRP 3.0)', textEn: 'Try to move opponent, but tense up on game/break points', points: 3.0 },
      { text: '熟练执行百分比网球战术，能发现对手弱点并实施针对性打击 (NTRP 3.5 - 4.0)', textEn: 'Execute percentage tennis, exploit opponent weaknesses methodically', points: 3.9 },
      { text: '抗压大心脏，关键分发球与变线极具杀伤力，战术应变自如 (NTRP 4.5+)', textEn: 'Clutch mental toughness, change strategies mid-match to turn tides', points: 4.8 }
    ]
  }
];

export interface TierInfo {
  tier: string;
  name: string;
  nameEn: string;
  icon: string;
  minExp: number;
  maxExp: number;
  ntrpEquivalent: string;
}

export const TIERS: TierInfo[] = [
  { tier: 'T1', name: '初学学徒', nameEn: 'Novice Trainee', icon: '🌱', minExp: 0, maxExp: 300, ntrpEquivalent: 'NTRP 1.5 - 2.0' },
  { tier: 'T2', name: '进阶球友', nameEn: 'Club Member', icon: '🎾', minExp: 300, maxExp: 800, ntrpEquivalent: 'NTRP 2.5 - 3.0' },
  { tier: 'T3', name: '俱乐部主力', nameEn: 'Joy Club Ace', icon: '⚡', minExp: 800, maxExp: 1800, ntrpEquivalent: 'NTRP 3.5' },
  { tier: 'T4', name: '竞技精英', nameEn: 'Competitive Elite', icon: '🔥', minExp: 1800, maxExp: 3500, ntrpEquivalent: 'NTRP 4.0' },
  { tier: 'T5', name: '巡回赛大师', nameEn: 'Tournament Master', icon: '👑', minExp: 3500, maxExp: 6000, ntrpEquivalent: 'NTRP 4.5' },
  { tier: 'T6', name: '欢乐传奇国手', nameEn: 'Joy Legend Champion', icon: '🏆', minExp: 6000, maxExp: 99999, ntrpEquivalent: 'NTRP 5.0+' },
];

export function getTierForExp(exp: number): TierInfo {
  for (let i = TIERS.length - 1; i >= 0; i--) {
    if (exp >= TIERS[i].minExp) {
      return TIERS[i];
    }
  }
  return TIERS[0];
}

export function generateSyncCode(): string {
  const chars = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

export function getInitialUserProfile(): UserProfile {
  return {
    id: 'user_' + Math.random().toString(36).substring(2, 9),
    syncCode: generateSyncCode(),
    name: '欢乐球友',
    avatar: '🎾',
    ntrpRating: 3.0,
    level: 3,
    exp: 420,
    matchesPlayed: 12,
    matchesWon: 8,
    drillsCompleted: 24,
    radarStats: {
      forehand: 68,
      backhand: 55,
      serve: 60,
      volley: 50,
      footwork: 65,
      tactics: 70,
      mental: 62,
    },
    courtTheme: 'clay',
    unlockedBadges: ['first_serve_ace', 'crosscourt_master', 'joy_club_member'],
  };
}

export function loadUserProfile(): UserProfile {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      return JSON.parse(raw);
    }
  } catch (e) {
    console.error('Failed to load profile', e);
  }
  const defaultProfile = getInitialUserProfile();
  saveUserProfile(defaultProfile);
  return defaultProfile;
}

export function saveUserProfile(profile: UserProfile): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
  } catch (e) {
    console.error('Failed to save profile', e);
  }
}
